import { useEffect, useState } from "react";

export default function useTitleLibrary({
  api,
  user,
  pageSize,
}) {
  const [titles, setTitles] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [titlesError, setTitlesError] = useState("");

  const fetchTitles = async ({ nextOffset = offset, nextSearch = searchTerm } = {}) => {
    if (!user) return;
    setLoadingTitles(true);
    setTitlesError("");
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(nextOffset),
      });
      if (nextSearch.trim()) params.set("q", nextSearch.trim());
      const res = await api.get(`/api/notes?${params.toString()}`);
      setTitles(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setTitlesError(err.response?.data?.error || "Unable to load note titles.");
    } finally {
      setLoadingTitles(false);
    }
  };

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    const loadTitles = async () => {
      setLoadingTitles(true);
      setTitlesError("");
      try {
        const params = new URLSearchParams({
          limit: String(pageSize),
          offset: String(offset),
        });
        if (searchTerm.trim()) params.set("q", searchTerm.trim());
        const res = await api.get(`/api/notes?${params.toString()}`);
        if (!cancelled) {
          setTitles(res.data.items || []);
          setTotal(res.data.total || 0);
        }
      } catch (err) {
        if (!cancelled) {
          setTitlesError(err.response?.data?.error || "Unable to load note titles.");
        }
      } finally {
        if (!cancelled) setLoadingTitles(false);
      }
    };

    loadTitles();
    return () => {
      cancelled = true;
    };
  }, [api, user, offset, searchTerm, pageSize]);

  useEffect(() => {
    if (!user) return undefined;

    const queryText = searchInput.trim();
    if (queryText.length < 2) {
      setSearchSuggestions([]);
      setLoadingSuggestions(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const params = new URLSearchParams({
          limit: "6",
          offset: "0",
          q: queryText,
        });
        const res = await api.get(`/api/notes?${params.toString()}`);
        if (!cancelled) {
          setSearchSuggestions(res.data.items || []);
        }
      } catch (_err) {
        if (!cancelled) {
          setSearchSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSuggestions(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, user, searchInput]);

  const resetTitleLibrary = () => {
    setTitles([]);
    setTotal(0);
    setOffset(0);
    setSearchInput("");
    setSearchTerm("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setLoadingSuggestions(false);
    setLoadingTitles(false);
    setTitlesError("");
  };

  const optimisticCreateTitle = (tempGroup) => {
    const marked = { ...tempGroup, temp: true };
    setTitles((current) => [marked, ...current].slice(0, pageSize));
    setTotal((current) => Number(current || 0) + 1);
  };

  const replaceTempTitle = (tempId, serverGroup) => {
    setTitles((current) => current.map((group) => (group.id === tempId ? serverGroup : group)));
  };

  const revertTempTitle = (tempId) => {
    setTitles((current) => current.filter((group) => group.id !== tempId));
    setTotal((current) => Math.max(0, Number(current || 0) - 1));
  };

  return {
    titles,
    total,
    offset,
    searchInput,
    searchTerm,
    searchSuggestions,
    showSuggestions,
    loadingSuggestions,
    loadingTitles,
    titlesError,
    setOffset,
    setSearchInput,
    setSearchTerm,
    setSearchSuggestions,
    setShowSuggestions,
    fetchTitles,
    resetTitleLibrary,
    optimisticCreateTitle,
    replaceTempTitle,
    revertTempTitle,
  };
}
